import { useEffect, useState } from "react";
import { X, Wrench, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "../lib";

/**
 * ConvertDialog — turns an estimate into a job card.
 *
 * Shows a preview of what will carry forward.  Lets the user pick which
 * lines to include (defaults to all non-optional).  Prevents accidental
 * duplicate conversion — server responds 409 which we surface here.
 */
export function ConvertDialog({ est, onClose, onConverted }) {
  const [team, setTeam] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [promisedAt, setPromisedAt] = useState("");
  const [selected, setSelected] = useState(() => {
    const s = new Set();
    for (const p of est.parts || []) if (!p.optional && p.id) s.add(p.id);
    for (const l of est.labor || []) if (!l.optional && l.id) s.add(l.id);
    return s;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/team").then((r) => setTeam((r.data || []).filter((u) => ["mechanic", "advisor"].includes(u.role))))
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const parts = est.parts || [];
  const labor = est.labor || [];

  const submit = async ({ confirm = false } = {}) => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        selected_line_ids: Array.from(selected),
        assigned_to: assignee || null,
        promised_at: promisedAt || null,
        confirm_duplicate: confirm,
      };
      const r = await api.post(`/estimates/${est.id}/convert`, body);
      onConverted?.(r.data);
    } catch (e) {
      const code = e.response?.status;
      const detail = e.response?.data?.detail;
      if (code === 409 && detail?.includes("Already converted")) {
        setError({ kind: "dup", detail });
      } else {
        setError({ kind: "other", detail: detail || "Conversion failed" });
      }
    } finally { setSaving(false); }
  };

  if (est.status === "converted" && !error) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
        <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-[16px] font-semibold text-[#1d3557]">Already converted</h3>
          <p className="text-[12.5px] text-[#667085] mt-1">
            This estimate has been converted to job card{" "}
            <a href={`/job-cards/${est.converted_to_job_card_id}`} className="text-[#12B76A] font-semibold hover:underline">
              #{est.converted_to_job_card_id?.slice(0, 8)}
            </a>
            .
          </p>
          <button
            type="button" onClick={onClose}
            className="mt-4 h-9 px-4 rounded-lg bg-[#1d3557] text-white text-[12.5px] font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="est-convert-dialog">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#F1FAEE]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#12B76A]/15 text-[#12B76A] grid place-items-center">
              <Wrench size={16} />
            </div>
            <div>
              <h3 className="text-[14.5px] font-semibold text-[#1d3557]">Convert to Job Card</h3>
              <p className="text-[11.5px] text-[#667085]">
                From {est.number} · {est.customer_snapshot?.name}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#a8dadc] hover:text-[#e63946]"><X size={16} /></button>
        </header>

        <div className="p-4 space-y-3 overflow-auto flex-1">
          {error?.kind === "dup" && (
            <div className="rounded-lg border border-[#F79009]/30 bg-[#F79009]/10 p-3">
              <div className="flex items-start gap-2 text-[12.5px] text-[#7c4a02] mb-2">
                <AlertOctagon size={14} className="mt-0.5 shrink-0" />
                <div>{error.detail}</div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button" onClick={() => submit({ confirm: true })}
                  disabled={saving}
                  className="h-8 px-3 rounded-md bg-[#F79009] hover:bg-[#dc7d05] text-white text-[11.5px] font-semibold"
                >
                  Convert anyway
                </button>
                <button
                  type="button" onClick={onClose}
                  className="h-8 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {error?.kind === "other" && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12.5px] text-[#8b1c25]">
              {error.detail}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Assign to</span>
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                data-testid="convert-assignee"
                className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px]"
              >
                <option value="">Unassigned</option>
                {team.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Expected delivery</span>
              <input
                type="datetime-local"
                value={promisedAt}
                onChange={(e) => setPromisedAt(e.target.value)}
                data-testid="convert-promised-at"
                className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px]"
              />
            </label>
          </div>

          {parts.length > 0 && (
            <SelectableList
              title="Parts"
              items={parts.map((p) => ({ id: p.id, label: p.name, sub: `${p.qty} × ${inr(p.unit_price)}`, optional: p.optional }))}
              selected={selected}
              onToggle={toggle}
              testId="convert-parts-list"
            />
          )}
          {labor.length > 0 && (
            <SelectableList
              title="Labour"
              items={labor.map((l) => ({ id: l.id, label: l.task_name, sub: `${l.qty} hr × ${inr(l.unit_price)}`, optional: l.optional }))}
              selected={selected}
              onToggle={toggle}
              testId="convert-labor-list"
            />
          )}
        </div>

        {error?.kind !== "dup" && (
          <div className="p-3 border-t border-[#F1FAEE] flex gap-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 h-10 rounded-lg border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557]"
            >
              Cancel
            </button>
            <button
              type="button" onClick={() => submit()}
              disabled={saving}
              data-testid="convert-submit"
              className="flex-1 h-10 rounded-lg bg-[#12B76A] hover:bg-[#0f9a58] text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60"
            >
              <Wrench size={13} /> {saving ? "Creating…" : "Create Job Card"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SelectableList({ title, items, selected, onToggle, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">{title}</div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.id}>
            <label className="flex items-center gap-2 p-2 rounded hover:bg-[#F1FAEE]/50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(it.id)}
                onChange={() => onToggle(it.id)}
                data-testid={`convert-toggle-${it.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#1d3557] truncate">
                  {it.label}
                  {it.optional && <span className="ml-1.5 text-[9.5px] font-semibold text-[#457b9d] uppercase">Optional</span>}
                </div>
                <div className="text-[11px] text-[#667085]">{it.sub}</div>
              </div>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
